import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Minus, Plus, Trash2, ClipboardList } from 'lucide-react';
import { img, labelize, PrimaryButton, GhostButton } from './ui';
import { useInquiryCart } from '../../../context/InquiryCartContext';
import InquiryFormModal from './InquiryFormModal';

/* Right-hand drawer that holds the inquiry cart. "Send inquiry" opens the
 * customer form modal; the whole thing funnels into the existing enquiry API. */
export default function InquiryCartDrawer() {
  const { items, count, open, closeCart, removeItem, setQty } = useInquiryCart();
  const [formOpen, setFormOpen] = useState(false);

  return (
    <>
      {/* overlay */}
      <div
        className={`fixed inset-0 z-[70] bg-[#160f0a80] transition-opacity duration-300 ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeCart}
      />
      {/* panel */}
      <aside
        className={`fixed top-0 right-0 z-[71] h-full w-full max-w-md bg-[#f4ece0] shadow-2xl flex flex-col transition-transform duration-400 ease-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
        aria-hidden={!open}
        aria-label="Inquiry cart"
      >
        <header className="flex items-center justify-between px-6 py-5 border-b border-[#241811]/12">
          <div className="flex items-center gap-2.5">
            <ClipboardList className="w-5 h-5 text-[#b5622f]" />
            <h2 className="font-display text-lg">Your inquiry</h2>
            <span className="text-xs text-[#4a3d32]">({count})</span>
          </div>
          <button onClick={closeCart} aria-label="Close inquiry" className="p-1 hover:text-[#b5622f]">
            <X />
          </button>
        </header>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
            <ClipboardList className="w-10 h-10 text-[#241811]/25 mb-4" />
            <p className="font-display text-lg mb-1">Your inquiry list is empty</p>
            <p className="text-sm text-[#4a3d32] mb-6">Add pieces you'd like a quote on and send them to us together.</p>
            <GhostButton to="/products" onClick={closeCart}>Browse the range</GhostButton>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto divide-y divide-[#241811]/10">
              {items.map((it) => (
                <div key={it.id} className="flex gap-4 p-5">
                  <Link to={`/products/${it.slug}`} onClick={closeCart} className="flex-shrink-0">
                    <img src={img(it.image_url)} alt={it.name} className="w-20 h-24 object-cover bg-[#ece0cd]" />
                  </Link>
                  <div className="flex-1 min-w-0">
                    {it.category && (
                      <div className="text-[10px] uppercase tracking-[0.16em] text-[#b5622f] mb-0.5">
                        {labelize(it.category)}
                      </div>
                    )}
                    <Link
                      to={`/products/${it.slug}`}
                      onClick={closeCart}
                      className="font-display text-[15px] leading-snug hover:text-[#b5622f] transition-colors block"
                    >
                      {it.name}
                    </Link>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-[#241811]/20">
                        <button
                          onClick={() => setQty(it.id, it.qty - 1)}
                          aria-label="Decrease quantity"
                          className="px-2 py-1.5 hover:bg-[#2418110d]"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="px-3 text-sm tabular-nums">{it.qty}</span>
                        <button
                          onClick={() => setQty(it.id, it.qty + 1)}
                          aria-label="Increase quantity"
                          className="px-2 py-1.5 hover:bg-[#2418110d]"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(it.id)}
                        aria-label="Remove item"
                        className="text-[#4a3d32] hover:text-[#b5622f] p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <footer className="border-t border-[#241811]/12 p-5">
              <p className="text-[12px] text-[#4a3d32] mb-3">
                No pricing shown — send your list and we'll reply with a tailored quotation.
              </p>
              <PrimaryButton onClick={() => setFormOpen(true)} className="w-full">
                Send inquiry
              </PrimaryButton>
            </footer>
          </>
        )}
      </aside>

      <InquiryFormModal open={formOpen} onClose={() => setFormOpen(false)} />
    </>
  );
}
